import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { useMedplum, useMedplumProfile } from "@medplum/react";

import { useFeatures } from "@/feature-flags";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import { getActiveOrganizationId } from "@/utils/medplum";

import type { DestinationAccess } from "../types/destinations";
import { getDestinationAudience } from "../utils/get-destination-audience";

/**
 * Builds the facts about the signed-in user and their active practice that decide which
 * destinations they can open. Reads the profile from `useMedplumProfile`, so it works in the
 * header, which renders outside `ProfileContext`.
 *
 * Call it only inside the app shell, which renders once a profile is signed in and a practice is
 * active. The practice comes from the route's `:practiceId` when the URL names one, and from the
 * stored active practice otherwise.
 *
 * @param isLabOrdersIntegrationActive - The caller's lab-integration lookup result; `undefined`
 * while the lookup is unresolved. Each caller owns when and whether it fetches.
 * @returns The access facts, stable while their inputs are unchanged.
 */
export function useDestinationAccess(isLabOrdersIntegrationActive?: boolean): DestinationAccess {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const features = useFeatures();
  const { permissions } = usePractitionerPermissions();
  // The client keeps one instance across sign-in and profile loads, so read the membership on
  // every render. The memo below keeps the returned object stable while the answer is unchanged.
  const isProjectAdmin = medplum.isProjectAdmin();

  const profileId = profile?.id;
  const audience = getDestinationAudience(profile);
  const { practiceId: routePracticeId } = useParams<{ practiceId?: string }>();
  const practiceId = routePracticeId ?? getActiveOrganizationId();

  const access = useMemo<DestinationAccess | undefined>(() => {
    if (!profileId) return undefined;
    return {
      audience,
      permissions,
      isProjectAdmin,
      practiceFeatures: features.practice,
      developmentFeatures: features.development,
      practiceId,
      profileId,
      isLabOrdersIntegrationActive,
    };
  }, [
    audience,
    permissions,
    isProjectAdmin,
    features.practice,
    features.development,
    practiceId,
    profileId,
    isLabOrdersIntegrationActive,
  ]);

  if (!access) throw new Error("useDestinationAccess needs a signed-in profile");
  return access;
}
